/**
 * BentoHome — iter87.1. The main tournament page IS a bento grid
 * (operator direction: tiles are the navigation, like the mockups).
 * Each tile is status-first and opens a full plane (Takeover).
 *
 * iter87.1 delta:
 *   • "My Picks" tile removed — its actionable states now live in the
 *     top-of-page `TodoChips` strip (Bonus Pick, Power Up, Boosts, Pot
 *     picks, Ranking). The plane is still reachable via those chips.
 *   • Admin tile removed — the floating DashboardMenu already carries
 *     Tournament Admin + Rivalry Admin entries; the tile was redundant.
 *   • Base tile fonts bumped one step for readability (label text-base,
 *     sub text-[11px], leaderboard rows text-sm).
 */
import { Link } from "react-router-dom";
import { useState } from "react";
import { motion } from "framer-motion";
import { isBoostOnlyTemplate } from "@/lib/boostOnlyTemplates";
import {
    List, Calendar, ListOrdered, GitBranch,
    BarChart2, Users, Sparkles, Flame, Triangle, Activity,
} from "lucide-react";
import { isGolfFormat } from "@/lib/tournamentFormat";
import { isPodiumMode, isLeaderEligible, rankBands } from "@/lib/leaderboardRanking";
import { BentoTile, BentoTileGrid } from "@/components/onui/BentoMotion";
import { isSquaresTemplate } from "@/components/dashboard/SquaresPanel";
import ScoringRulesModal from "@/components/dashboard/ScoringRulesModal";

const MotionLink = motion(Link);

const Tile = ({ icon: Icon, label, sub, onClick, to, testId, badge, badgeTestId, attention, attentionTestId, span = "", hideFooter = false, children }) => {
    const cls = `relative rounded-2xl border border-white/[0.08] bg-[#121212] p-4 text-left min-h-[120px] flex flex-col justify-between gap-3 hover:border-cyber/60 hover:bg-[#161616] ${span}`;
    const body = (
        <>
            <span className="flex items-center justify-between">
                <span className="w-9 h-9 rounded-full bg-cyber/10 border border-cyber/40 flex items-center justify-center">
                    <Icon className="w-4 h-4 text-cyber" />
                </span>
                {badge != null && (
                    <span data-testid={badgeTestId} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-neon text-black text-[11px] font-bold uppercase tracking-wider">
                        <span className="w-1 h-1 rounded-full bg-black animate-pulse" />{badge}
                    </span>
                )}
            </span>
            {children}
            {!hideFooter && (
                <span>
                    <span className="block font-display font-extrabold text-base uppercase tracking-tight leading-tight">{label}</span>
                    {sub && <span className="block text-[11px] text-zinc-500 mt-0.5 truncate">{sub}</span>}
                </span>
            )}
            {attention && (
                <span data-testid={attentionTestId} className="absolute top-2 right-2 w-2.5 h-2.5 rounded-full bg-volt border border-black animate-pulse" />
            )}
        </>
    );
    if (to) {
        return (
            <MotionLink
                to={to}
                data-testid={testId}
                whileHover={{ y: -2, scale: 1.01 }}
                whileTap={{ scale: 0.975 }}
                transition={{ type: "spring", stiffness: 380, damping: 32 }}
                className={cls}
            >
                {body}
            </MotionLink>
        );
    }
    return (
        <BentoTile as="button" onClick={onClick} testId={testId} className={cls}>
            {body}
        </BentoTile>
    );
};

export default function BentoHome({
    leagueId,
    tournament,
    me,
    board,
    myRow,
    myTeams,
    liveCount,
    showStandingsTab,
    showBracketTab,
    champion,
    roundMovers,
    onOpen,
    squaresState,
}) {
    const isGolf = isGolfFormat(tournament);
    // iter87.599/600 (user-requested) — a Squares game has exactly one
    // fixture, so its Cheer-section tile is permanently "Game of
    // Squares" (live score sheet + locked grid + pot) instead of the
    // generic multi-fixture Schedule tile.
    const isSquares = isSquaresTemplate(tournament?.template);
    // iter87.52 → iter87.111 — Boost-only templates (CFB Weekly + NFL
    // Weekly Spread Boost) have no team assignments — the entire game
    // surface is the weekly boost picker. Hide the My Teams tile for
    // these templates so the Bento grid doesn't render an empty pane.
    const isCfbBoost = isBoostOnlyTemplate(tournament?.template);
    const [scoringRulesOpen, setScoringRulesOpen] = useState(false);
    const scoreOf = (r) => isGolf
        ? (r.golf?.avg_score != null ? r.golf.avg_score.toFixed(2) : "—")
        : r.total_points;

    // iter87.198 — CHEER bento layout rebalanced. The previous
    // Leaderboard(2-col hero) + singles + dynamic-Recap-span layout
    // left sloppy gaps on iPad (the old `md` 3-col grid) whenever the
    // Standings/Bracket/MyTeams count didn't align cleanly.
    // iter87.198.1 — Grid capped at 2 columns on every breakpoint
    // (3-across was too big for a hero-content section, per user
    // feedback). New layout:
    //   Row 1 · Leaderboard — full width (col-span-2)
    //   Row 2+ · Schedule / Standings / Bracket / My Teams — 1 col each,
    //           auto-flow through however many rows they need
    //   Last row · Recap — spans 2 if it lands alone, spans 1 if it
    //             lands next to a leftover single (so the grid never
    //             leaves a sloppy gap next to it).
    // Recap's alone-on-last-row math: it lands ALONE when the number
    // of singles is even (0, 2, or 4). Odd singles (1, 3) mean the
    // last single sits alone on the last-but-one row, so Recap pairs
    // with a would-be-empty slot on its own row.
    const FULL_WIDTH_SPAN = "col-span-2";
    const singlesCount = [
        !isGolf,                                              // Schedule
        showStandingsTab,                                     // Standings
        showBracketTab,                                       // Bracket
        !!(me && !isGolf && !isCfbBoost && myTeams.length > 0),  // My Teams
    ].filter(Boolean).length;
    const recapAlone = singlesCount % 2 === 0;
    const recapSpanClass = recapAlone ? "col-span-2" : "col-span-1";

    // iter87.415 (operator-corrected again — "I want the new UI for
    // everything ... the bars gone") — retiring the old gold/silver/
    // bronze bar-chart preview entirely. The ticker tile from
    // iter87.412 is now used for every tournament; `podiumMode` only
    // switches its INTERNAL content between a tie-aware "Top 3" list
    // (Podium Trophy Tokens: game_type === "podium") and a single
    // tie-aware "Leader(s)" line (everything else — TT off, or TT on
    // with game_type === "winner_take_all").
    const podiumMode = isPodiumMode(tournament);

    return (
        <div data-testid="bento-home" className="space-y-2">
            <div className="flex items-center justify-between px-1">
                <p className="text-base sm:text-lg uppercase tracking-[0.18em] text-white font-bold flex items-center gap-2">
                    <Flame className="w-5 h-5" /> Cheer
                </p>
                {/* iter87.199.3 — Scoring Rules pill moved to the right
                    side of the header (was on the left next to the
                    Cheer title). Tighter border + a pulsing cyan dot
                    so it visually rhymes with the LIVE UPDATES pill it
                    replaces. Testid preserved for pytest guardrails.
                    Live-updates signal is folded into the Schedule tile
                    below (see `wsActiveIndicator` on the Schedule Tile). */}
                <button
                    type="button"
                    onClick={() => setScoringRulesOpen(true)}
                    data-testid="cheer-scoring-info-btn"
                    title="How scoring works for this tournament"
                    aria-label="Open Scoring Rules"
                    className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] leading-none font-bold uppercase tracking-wider border border-cyan-400/60 text-cyan-300 bg-cyan-500/10 hover:bg-cyan-500/20 hover:border-cyan-400 transition-colors"
                >
                    <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
                    Scoring Rules
                </button>
            </div>

            <BentoTileGrid className="grid grid-cols-2 gap-3">
                {/* Leaderboard — hero tile, full width on every breakpoint
                    (iter87.198; bar-chart preview retired in iter87.415 —
                    see LeaderboardTickerPreview below). */}
                <Tile
                    icon={List}
                    label="Leaderboard"
                    onClick={() => onOpen("leaderboard")}
                    testId="view-leaderboard-btn"
                    span={FULL_WIDTH_SPAN}
                    hideFooter
                >
                    <LeaderboardTickerPreview
                        board={board}
                        myRow={myRow}
                        meId={me?.id}
                        isGolf={isGolf}
                        scoreOf={scoreOf}
                        roundMovers={roundMovers}
                        podiumMode={podiumMode}
                    />
                </Tile>

                {!isGolf && (
                    isSquares ? (
                        // iter87.600 (user-requested) — a Squares board
                        // is always exactly ONE game, so the generic
                        // multi-fixture "Schedule" tile never made
                        // sense here. This tile is now permanently
                        // "Game of Squares" (visible pre-kickoff too,
                        // showing the pending matchup) instead of
                        // swapping in only once the game starts.
                        <Tile
                            icon={Activity}
                            label="Game of Squares"
                            sub="Game & Results"
                            onClick={() => onOpen("game-progress")}
                            testId="view-game-progress-btn"
                            badge={squaresState?.game_started ? "LIVE" : null}
                            badgeTestId="view-game-progress-live-badge"
                        />
                    ) : (
                        <Tile
                            icon={Calendar}
                            label="Schedule"
                            sub={liveCount > 0 ? "Games live right now" : "Fixtures & results"}
                            onClick={() => onOpen("schedule")}
                            testId="view-schedule-btn"
                            badge={liveCount > 0 ? liveCount : null}
                            badgeTestId="view-schedule-live-badge"
                        />
                    )
                )}
                {showStandingsTab && (
                    <Tile icon={ListOrdered} label="Standings" sub={isGolf ? "Full golfer field" : "Table & form"} onClick={() => onOpen("standings")} testId="view-standings-btn" />
                )}
                {showBracketTab && (
                    <Tile icon={GitBranch} label="Bracket" sub="Knockout rounds" onClick={() => onOpen("bracket")} testId="view-bracket-btn" />
                )}
                {me && !isGolf && !isCfbBoost && myTeams.length > 0 && (
                    <Tile
                        icon={Users}
                        label="My Teams"
                        sub={`${myTeams.length} on your card`}
                        onClick={() => onOpen("teams")}
                        testId="view-teams-btn"
                    />
                )}
                <Tile
                    icon={Sparkles}
                    label="Recap"
                    sub={champion ? `Champion: ${champion.name}` : "Awards & points chart"}
                    to={`/league/${leagueId}/recap${tournament?.id ? `?tournament_id=${tournament.id}` : ""}`}
                    testId="view-recap-link"
                    span={recapSpanClass}
                />
            </BentoTileGrid>
            <ScoringRulesModal
                open={scoringRulesOpen}
                onClose={() => setScoringRulesOpen(false)}
                tournament={tournament}
            />
        </div>
    );
}

/**
 * iter87.412 → iter87.415 (operator-requested, bars retired entirely) —
 * compact Leaderboard tile preview, now a single stock-ticker-style
 * component for every tournament: a YOU row (name, "(You)" tag, score,
 * a rank-movement triangle vs. the most recent round — sourced from
 * the SAME `roundMovers` payload the Biggest Movers widget uses, so no
 * fabricated data) + a plain hairline divider + de-emphasized info
 * below, split by `podiumMode`:
 *   • Podium Trophy Tokens (`podiumMode=true`) → "Top 3" — up to 3
 *     tie-aware ordinal rows (1st/2nd/3rd).
 *   • Everything else — TT off, or TT on with game_type
 *     "winner_take_all" (`podiumMode=false`) → "Leader(s)" (with a
 *     "1st" tag next to their score) + "The Field" average + "Last"
 *     (whoever's in last place) — a single tie-aware row each.
 *
 * iter87.453 (user-requested): the YOU row's trend chip now also
 * renders for a true zero delta (a muted "—" instead of being hidden
 * entirely — "even" is a real state, not "no data"), the "(You)" tag
 * is back (blue, `text-volt`), the Leader(s) row gets a small "1st"
 * next to its score, and a new "Last" row (same tie-collapse wording
 * as Leader(s)) sits below "The Field".
 *
 * Tie-collapse wording (shared by Leader(s) and Last): 1 player →
 * their name; 2 players → "NameA & NameB" (falls back to "2 players
 * tied" if the combined string is too long); 3+ → generic "N players
 * tied" unless the viewer is one of them, in which case it
 * personalizes to "{viewer name} + N others tied" and tints the
 * viewer's name blue.
 */
function bandLabelNode(members, meId) {
    const isMe = (m) => m.participant_id === meId;
    const nameNode = (m, key) => <span key={key} className={isMe(m) ? "text-volt" : undefined}>{m.username}</span>;
    if (members.length === 1) return nameNode(members[0], "n0");
    if (members.length === 2) {
        const combined = `${members[0].username} & ${members[1].username}`;
        if (combined.length > 22) return "2 players tied";
        return <>{nameNode(members[0], "n0")} &amp; {nameNode(members[1], "n1")}</>;
    }
    const me = members.find(isMe);
    if (me) return <>{nameNode(me, "n0")} + {members.length - 1} others tied</>;
    return `${members.length} players tied`;
}

const ORDINALS = ["1st", "2nd", "3rd"];

function LeaderboardTickerPreview({ board, myRow, meId, isGolf, scoreOf, roundMovers, podiumMode }) {
    const captionOf = isGolf ? "AVG" : "PTS";
    const bands = podiumMode ? rankBands(board.filter((r) => r.rank <= 3), 3) : rankBands(board.filter((r) => r.rank === 1), 1);

    // iter87.417 (operator-requested) — reads the viewer's own delta
    // directly from `roundMovers.deltas` (every participant, not just
    // the top-5 "Biggest Movers") so the trend arrow reliably shows
    // real movement for the main player regardless of how big a mover
    // they are.
    const myDelta = myRow ? roundMovers?.deltas?.[myRow.participant_id] : undefined;

    // iter87.417 (operator-requested) — "The Field" line (non-Podium
    // only): average score of everyone EXCEPT the leader band.
    const fieldRows = !podiumMode && bands.length ? board.filter((r) => r.rank !== 1) : [];
    const fieldAvg = fieldRows.length
        ? fieldRows.reduce((sum, r) => sum + (Number(scoreOf(r)) || 0), 0) / fieldRows.length
        : null;
    const fieldAvgDisplay = fieldAvg === null ? null : (isGolf ? fieldAvg.toFixed(1) : Math.round(fieldAvg));

    // iter87.453 (user-requested) — "Last" line (non-Podium only):
    // whoever's in LAST place, same tie-collapse wording as the
    // leader line (`bandLabelNode`). Skipped when the whole field is
    // tied for 1st (maxRank === 1 — there's no separate "last" then).
    const maxRank = board.length ? Math.max(...board.map((r) => r.rank)) : null;
    const lastBand = !podiumMode && maxRank !== null && maxRank !== 1 ? board.filter((r) => r.rank === maxRank) : [];

    return (
        <span className="block space-y-2.5" data-testid="leaderboard-ticker-preview">
            <span>
                <span className="block font-display font-extrabold text-base uppercase tracking-tight leading-tight">Leaderboard</span>
                <span className="block text-[11px] text-zinc-500 mt-0.5">Ranking &amp; points</span>
            </span>
            {myRow && (
                <span className="flex items-center justify-between gap-2 py-2.5 px-3 rounded-xl bg-[#111113]" data-testid="leaderboard-ticker-you-row">
                    <span className="flex items-center gap-1.5 min-w-0">
                        {myRow.avatar_emoji && (
                            <span className="text-sm leading-none shrink-0" aria-hidden="true">{myRow.avatar_emoji}</span>
                        )}
                        <span className="text-sm font-bold text-white truncate">{myRow.username}</span>
                        <span className="text-[10px] font-bold text-volt shrink-0" data-testid="leaderboard-ticker-you-label">(You)</span>
                    </span>
                    <span className="flex flex-col items-end shrink-0 leading-none gap-0.5">
                        <span className="text-[9px] text-zinc-500 font-mono" data-testid="leaderboard-ticker-you-rank">
                            #{myRow.rank} of {board.length}
                        </span>
                        <span className="flex items-center gap-1.5">
                            <span className="font-mono font-black text-sm text-white">
                                {scoreOf(myRow)} <span className="text-[9px] text-zinc-500 font-bold">{captionOf}</span>
                            </span>
                            {myDelta !== undefined && (
                                myDelta === 0 ? (
                                    <span
                                        className="inline-flex items-center text-[10px] font-mono font-bold text-zinc-500"
                                        data-testid="leaderboard-ticker-you-trend"
                                        title="Even since last round"
                                    >
                                        —
                                    </span>
                                ) : (
                                    <span
                                        className={`inline-flex items-center gap-0.5 text-[10px] font-mono font-bold ${myDelta > 0 ? "text-neon" : "text-heat"}`}
                                        data-testid="leaderboard-ticker-you-trend"
                                        title={`${myDelta > 0 ? "Up" : "Down"} ${Math.abs(myDelta)} since last round`}
                                    >
                                        <Triangle className={`w-2 h-2 ${myDelta > 0 ? "" : "rotate-180"}`} fill="currentColor" strokeWidth={0} />
                                        {Math.abs(myDelta)}
                                    </span>
                                )
                            )}
                        </span>
                    </span>
                </span>
            )}
            {bands.length > 0 && (
                <>
                    <span className="block border-t border-white/[0.08]" data-testid="leaderboard-ticker-divider" />
                    {podiumMode ? (
                        <span className="block px-3" data-testid="leaderboard-ticker-top3">
                            <span className="block text-[9px] uppercase tracking-widest text-zinc-500 mb-1">Top 3</span>
                            <span className="block space-y-0.5">
                                {bands.map(([rank, members], idx) => (
                                    <span key={rank} className="flex items-center justify-between gap-2" data-testid={`leaderboard-ticker-top3-row-${idx + 1}`}>
                                        <span className="flex items-center gap-1.5 min-w-0">
                                            <span className="text-[10px] text-zinc-600 shrink-0 w-6">{isLeaderEligible(members[0], podiumMode) ? ORDINALS[idx] : ""}</span>
                                            <span className="text-xs text-zinc-400 truncate">{bandLabelNode(members, meId)}</span>
                                        </span>
                                        <span className="text-xs font-mono text-zinc-500 shrink-0">{scoreOf(members[0])} {captionOf.toLowerCase()}</span>
                                    </span>
                                ))}
                            </span>
                        </span>
                    ) : (
                        <span className="block px-3 space-y-1.5" data-testid="leaderboard-ticker-leaders">
                            <span className="block">
                                <span className="block text-[9px] uppercase tracking-widest text-zinc-500 mb-1">Leader(s)</span>
                                <span className="flex items-center justify-between">
                                    <span className="text-xs text-zinc-400 truncate" data-testid="leaderboard-ticker-leaders-label">{bandLabelNode(bands[0][1], meId)}</span>
                                    <span className="flex items-center gap-1 shrink-0">
                                        <span className="text-xs font-mono text-zinc-500">{scoreOf(bands[0][1][0])} {captionOf.toLowerCase()}</span>
                                        {isLeaderEligible(bands[0][1][0], podiumMode) && (
                                            <span className="text-[9px] font-bold text-zinc-600" data-testid="leaderboard-ticker-leaders-ordinal">1st</span>
                                        )}
                                    </span>
                                </span>
                            </span>
                            {fieldAvgDisplay !== null && (
                                <span className="flex items-center justify-between" data-testid="leaderboard-ticker-field">
                                    <span className="text-xs text-zinc-500 truncate">The Field</span>
                                    <span className="flex items-center gap-1 shrink-0">
                                        <span className="text-xs font-mono text-zinc-600">{fieldAvgDisplay}{isGolf ? "" : ` ${captionOf.toLowerCase()}`}</span>
                                        <span className="text-[9px] font-bold text-zinc-600" data-testid="leaderboard-ticker-field-avg-label">Avg</span>
                                    </span>
                                </span>
                            )}
                            {lastBand.length > 0 && (
                                <span className="flex items-center justify-between" data-testid="leaderboard-ticker-last">
                                    <span className="text-xs text-zinc-500 truncate">{bandLabelNode(lastBand, meId)}</span>
                                    <span className="flex items-center gap-1 shrink-0">
                                        <span className="text-xs font-mono text-zinc-600">{scoreOf(lastBand[0])} {captionOf.toLowerCase()}</span>
                                        <span className="text-[9px] font-bold text-zinc-600" data-testid="leaderboard-ticker-last-badge">Last</span>
                                    </span>
                                </span>
                            )}
                        </span>
                    )}
                </>
            )}
        </span>
    );
}
